import { useEffect, useState } from "react";
import {
  Sun,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  Moon,
} from "lucide-react";
import { SITE, bootDate } from "../data/stories";

const clockFmt = new Intl.DateTimeFormat("en-GB", {
  weekday: "long", day: "numeric", month: "long", year: "numeric",
  timeZone: "Africa/Banjul",
});

function SolidWeatherIcon({ condition = "partlycloudy" }) {
  const clear = ["clear", "clearsky"].includes(condition);
  const rain = /rain|shower/.test(condition);
  const thunder = /thunder/.test(condition);
  const partly = ["fair", "partlycloudy"].includes(condition);
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-[#f5cf25] shrink-0" aria-hidden="true">
    {(clear || partly) && <>
      <circle cx={clear ? 12 : 8} cy={clear ? 12 : 8} r={clear ? 5 : 4} />
      {clear ? <path d="M11 0h2v4h-2zm0 20h2v4h-2zM0 11h4v2H0zm20 0h4v2h-4zM3 4.4 4.4 3l2.8 2.8-1.4 1.4zm13.8 13.8 1.4-1.4 2.8 2.8-1.4 1.4zM3 19.6l2.8-2.8 1.4 1.4L4.4 21zM16.8 5.8 19.6 3 21 4.4l-2.8 2.8z" /> : <path d="M7 0h2v3H7zM0 7h3v2H0zm1.7-3.9 1.4-1.4 2.1 2.1-1.4 1.4zm10.5.7 2.1-2.1 1.4 1.4-2.1 2.1z" />}
    </>}
    {!clear && <path d="M6 18a5 5 0 0 1-.7-9.95A6.5 6.5 0 0 1 18 9.1a4.5 4.5 0 1 1 1.5 8.9H6z" />}
    {rain && <path d="m7 19-2 4h2l2-4zm5 0-2 4h2l2-4zm5 0-2 4h2l2-4z" />}
    {thunder && <path d="m12 16-3 5h3l-1 3 6-6h-4l1-2z" />}
  </svg>;
}

export const TopBar = () => {
  const [theme, setTheme] = useState(() =>
    "light"
  );
  useEffect(() => {
    const sync = () => setTheme(document.documentElement.classList.contains("dark") ? "dark" : "light");
    sync(); window.addEventListener('standard-theme-change', sync); window.addEventListener('storage', sync);
    return () => {window.removeEventListener('standard-theme-change', sync);window.removeEventListener('storage', sync);};
  }, []);
  const [showThemeTip, setShowThemeTip] = useState(false);
  useEffect(() => {
    let hideTimer;
    const timer = setTimeout(() => {
      try { if(localStorage.getItem("standard-theme-tip-seen")) return; localStorage.setItem("standard-theme-tip-seen", "1"); } catch {}
      setShowThemeTip(true);
      hideTimer=setTimeout(()=>setShowThemeTip(false),10000);
    },1200);
    return () => {clearTimeout(timer);clearTimeout(hideTimer);};
  },[]);
  const [weather, setWeather] = useState(null);
  const [clock, setClock] = useState(() => clockFmt.format(new Date(bootDate || Date.now())));

  const toggleTheme = () => {
    setShowThemeTip(false);
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    document.documentElement.classList.toggle("dark", next === "dark");
    window.dispatchEvent(new Event("standard-theme-change"));
    try {
      localStorage.setItem("std-theme", next);
    } catch (e) {}
  };

  /* Production MET Norway weather feed and the Banjul calendar date. */
  useEffect(() => {
    let live = true;
    const load = async () => {
      try {const response=await fetch('/api/weather'); if(response.ok){const data=await response.json();if(live)setWeather(data);}}catch{}
    };
    load();
    const w = setInterval(load, 600000);
    const t = setInterval(() => setClock(clockFmt.format(new Date())), 20000);
    return () => {
      live = false;
      clearInterval(w);
      clearInterval(t);
    };
  }, []);



  return (
    <div
      data-testid="top-bar"
      className="relative z-[60] bg-[#3b0005] text-[#f7dddd] text-[11px] sm:text-[12px] font-medium"
    >
      <div className="mx-auto w-full max-w-[1640px] px-4 sm:px-[26px] flex items-center justify-between min-h-[34px]">
        <span
          data-testid="top-bar-weather"
          title={weather ? weather.condition : "Live weather for Banjul"}
          className="flex items-center gap-1.5 whitespace-nowrap"
        >
          <SolidWeatherIcon condition={weather?.icon} />
          <span>Banjul</span>
          {weather && weather.temperature != null && (
            <span className="tabular-nums" data-testid="top-bar-temperature">
              · {weather.temperature}°C
            </span>
          )}
        </span>
        <time className="ml-3 min-w-0 flex-1 truncate text-[#e8c6c8]" data-testid="top-bar-date">{clock}</time>
        <div className="flex items-center gap-4" aria-label="Social media">
          <a
            data-testid="top-bar-facebook"
            aria-label="Facebook"
            href={SITE.socials.facebook}
            target="_blank"
            rel="noreferrer"
            className="hover:text-white transition-colors"
          >
            <Facebook size={13} />
          </a>
          <a
            data-testid="top-bar-instagram"
            aria-label="Instagram"
            href="https://www.instagram.com/standardgambia/"
            target="_blank"
            rel="noreferrer"
            className="hover:text-white transition-colors"
          >
            <Instagram size={13} />
          </a>
          <a
            data-testid="top-bar-x"
            aria-label="X"
            href={SITE.socials.x}
            target="_blank"
            rel="noreferrer"
            className="hover:text-white transition-colors"
          >
            <Twitter size={13} />
          </a>
          <a
            data-testid="top-bar-youtube"
            aria-label="YouTube"
            href={SITE.socials.youtube}
            target="_blank"
            rel="noreferrer"
            className="hover:text-white transition-colors"
          >
            <Youtube size={14} />
          </a>
          <span className="h-3 w-px bg-white/25" />
          <div className="relative z-[80]">
          <button
            onClick={toggleTheme}
            aria-describedby={showThemeTip ? "theme-first-visit-tip" : undefined}
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            data-testid="theme-toggle"
            className="p-0.5 hover:text-white transition-colors"
          >
            {theme === "dark" ? <Sun size={13} /> : <Moon size={13} />}
          </button>
          {showThemeTip && <div id="theme-first-visit-tip" role="status" className="theme-mode-tip absolute right-[-4px] top-[calc(100%+14px)] w-[230px] rounded-[8px] border border-[#e5e7eb] bg-white p-4 text-[#141618] shadow-xl">
            <span aria-hidden="true" className="absolute -top-[6px] right-[8px] h-3 w-3 rotate-45 border-l border-t border-[#e5e7eb] bg-white" />
            <button type="button" onClick={()=>setShowThemeTip(false)} aria-label="Dismiss theme tip" className="absolute right-2 top-1 text-[18px] leading-none text-[#888] hover:text-[#cf0a0a] cursor-pointer">×</button>
            <p className="pr-3 text-[13px] font-bold">Light Or Dark? Your Choice.</p>
            <p className="mt-1.5 text-[12px] leading-[1.5] text-[#666]">Use this switch to choose the look you prefer.</p>
          </div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopBar;
